import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FullScreenLoading } from "../../components/ui/loader";
import { getApiErrorMessage } from "../../utils/apiErrorMessage";
import { deleteLeavePolicy, getLeavePolicies, LeavePolicyListItem } from "../../services/leavePolicyService";
import {
  CalendarDays,
  Edit3,
  FileText,
  Plus,
  Search,
  Trash2,
  X,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import useDebounce from "../../hooks/useDebounce";
import Pagination from "../../ui/pagination";
import { Badge } from "../../ui/badge";
import { usePageHeader } from "../../contexts/PageHeaderContext";
import { Button } from "../../ui/button";
import { Card, CardContent } from "../../ui/card";
import ConfirmDialog from "../../ui/ConfirmDialog";
import { Input } from "../../ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../ui/table";

export function LeaveStructuresPage() {
  const navigate = useNavigate();
  const { setPageHeader } = usePageHeader();
  const [policies, setPolicies] = useState<LeavePolicyListItem[]>([]);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search.trim(), 400);
  const [query, setQuery] = useState({ PolicyName: "", PageNumber: 1, PageSize: 20 });
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [refresh, setRefresh] = useState(0);
  const [deleting, setDeleting] = useState(false);
  const deletingRef = useRef(false);
  const [policyToDelete, setPolicyToDelete] = useState<LeavePolicyListItem | null>(
    null,
  );

  useEffect(() => {
    setPageHeader({
      title: "Leaves",
      icon: <CalendarDays className="h-5 w-5" />,
    });
    return () => setPageHeader(null);
  }, [setPageHeader]);

  useEffect(() => {
    setQuery((current) => current.PolicyName === debouncedSearch ? current : {
      ...current, PolicyName: debouncedSearch, PageNumber: 1,
    });
  }, [debouncedSearch]);

  useEffect(() => {
    if (query.PolicyName !== debouncedSearch) return;
    let cancelled = false;
    setLoading(true);
    setLoadError("");
    getLeavePolicies({ ...query, PolicyName: query.PolicyName || undefined })
      .then((result) => {
        if (cancelled) return;
        const lastPage = Math.max(1, Math.ceil(result.totalRecords / query.PageSize));
        if (query.PageNumber > lastPage) {
          setQuery((current) => ({ ...current, PageNumber: lastPage }));
          return;
        }
        setPolicies(result.data);
        setTotalRecords(result.totalRecords);
      })
      .catch((error) => {
        if (cancelled) return;
        setPolicies([]);
        setTotalRecords(0);
        setLoadError("Unable to load leave policies.");
        toast.error(getApiErrorMessage(error, "Unable to load leave policies."));
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [query, debouncedSearch, refresh]);

  const removePolicy = async () => {
    if (!policyToDelete || deletingRef.current) return;
    if (!policyToDelete.publicId) {
      toast.error("This policy has no backend public ID and cannot be deleted.");
      return;
    }
    deletingRef.current = true;
    setDeleting(true);
    try {
      await deleteLeavePolicy(policyToDelete.publicId);
      setLoading(true);
      setRefresh((value) => value + 1);
      setPolicyToDelete(null);
      toast.success("Leave policy deleted.");
    } catch (error) {
      const message = getApiErrorMessage(error, "Failed to delete leave policy.");
      toast.error(message);
    } finally {
      deletingRef.current = false;
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-6 pb-8">
      {(loading || deleting) && createPortal(
        <div className="fixed inset-0 z-[100]">
          <FullScreenLoading title={deleting ? "Deleting leave policy..." : "Loading leave policies..."} />
        </div>,
        document.body,
      )}
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          onClick={() => navigate("/leave-type")}
          className="h-9 rounded-full text-[#1e3a5f] hover:bg-sky-50"
        >
          <CalendarDays className="h-4 w-4" />
          Leave Type
        </Button>
        <Button className="h-9 rounded-full bg-[#5B9BD5] px-4 text-white hover:bg-[#4A8CC7]">
          <FileText className="h-4 w-4" />
          Leave Policy
        </Button>
      </div>
      <div className="flex flex-col gap-3 sm:flex-row mb-3">
        <div className="relative flex-1 mb-3">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <Input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search by policy name"
            className="h-10 bg-white pl-9 pr-9 shadow-sm"
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        <Button
          onClick={() => navigate("/leave-policies/new")}
          className="h-10 bg-[#5B9BD5] text-white hover:bg-[#4A8CC7]"
        >
          <Plus className="h-4 w-4" />
          Add Policy
        </Button>
      </div>
      <Card
        className="overflow-hidden border-sky-200 bg-white shadow-sm"
        style={{ marginTop: 0 }}
      >
        <CardContent className="p-0">
          <Table className="min-w-[720px]">
            <TableHeader>
              <TableRow className="bg-[#e3f2fd] hover:bg-[#e3f2fd]">
                <TableHead className="px-6 py-3 text-xs font-bold text-[#075985]">
                  Policy Name
                </TableHead>
                <TableHead className="px-6 py-3 text-xs font-bold text-[#075985]">
                  Employee Type
                </TableHead>
                <TableHead className="px-6 py-3 text-xs font-bold text-[#075985]">
                  Employment Status
                </TableHead>
                <TableHead className="px-6 py-3 text-xs font-bold text-[#075985]">
                  Grades
                </TableHead>
                <TableHead className="px-6 py-3 text-xs font-bold text-[#075985]">
                  Status
                </TableHead>
                <TableHead className="px-6 py-3 text-right text-xs font-bold text-[#075985]">
                  Actions
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(!loading && !loadError ? policies : []).map((policy) => (
                <TableRow
                  key={policy.publicId}
                  className="border-slate-200 hover:bg-sky-50/30"
                >
                  <TableCell className="px-6 py-4 text-xs font-medium text-slate-700">
                    {policy.policyName}
                  </TableCell>
                  <TableCell className="px-6 py-4 text-xs text-slate-600">
                    {policy.employeeTypeName || "—"}
                  </TableCell>
                  <TableCell className="px-6 py-4 text-xs text-slate-600">
                    {policy.employmentStatusName || "—"}
                  </TableCell>
                  <TableCell className="px-6 py-4 text-xs text-slate-600">
                    {policy.gradeName || "—"}
                  </TableCell>
                  <TableCell className="px-6 py-4 text-xs">
                    <Badge className={policy.isActive
                      ? "border border-emerald-300 bg-emerald-50 text-emerald-700"
                      : "border border-rose-300 bg-rose-50 text-rose-700"}>
                      {policy.isActive ? "Active" : "Inactive"}
                    </Badge>
                  </TableCell>
                  <TableCell className="px-6 py-3">
                    <div className="flex justify-end gap-1">
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => navigate(`/leave-policies/${policy.publicId}`)}
                        className="h-8 w-8 text-[#5B9BD5] hover:bg-sky-50"
                      >
                        <Edit3 className="h-4 w-4" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => setPolicyToDelete(policy)}
                        disabled={deleting}
                        className="h-8 w-8 text-red-500 hover:bg-red-50"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {(loading || loadError || policies.length === 0) && (
                <TableRow>
                  <TableCell
                    colSpan={6}
                    className="px-6 py-10 text-center text-sm text-slate-500"
                  >
                    {loading ? "Loading leave policies..." : loadError || "No leave policies found."}
                    {loadError && <Button variant="ghost" onClick={() => setRefresh((value) => value + 1)}>Retry</Button>}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
        <Pagination
          totalItems={totalRecords}
          itemsPerPage={query.PageSize}
          currentPage={query.PageNumber}
          onPageChange={(page) => setQuery((current) => ({ ...current, PageNumber: page }))}
          onPageSizeChange={(size) => setQuery((current) => ({ ...current, PageSize: size, PageNumber: 1 }))}
          label="leave policies"
        />
      </Card>
      <ConfirmDialog
        open={!!policyToDelete}
        onOpenChange={(open) => !open && !deletingRef.current && setPolicyToDelete(null)}
        title="Delete Leave Policy"
        description={
          <>Delete “{policyToDelete?.policyName}” and its entitlements?</>
        }
        confirmText="Delete"
        onConfirm={removePolicy}
        loading={deleting}
      />
    </div>
  );
}
